import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  inject,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ILayoutAssignmentForm,
  ISortimentForm,
  ITicketTileForm,
} from '../../../../entities/sortiment-form.entity';
import {
  EEntityType,
  ERelationType,
  ICmsTicketOverviewItem,
  ILayoutAssignment,
} from '@web/dtos';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { ChipComponent } from '../../../../components/data-display/chip/chip.component';
import { getTicketSortIcon } from '@web/shared-functions/sort-group';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { ButtonComponent } from '../../../../components/feedback/button/button.component';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { CheckboxComponent } from '../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { ImageSelectComponent } from '../../../../components/data-input/form-inputs/image-select/image-select.component';
import { TranslationAccessibilityTextInputComponent } from '../../../../components/data-input/form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextInputComponent } from '../../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { TranslationTextEditorComponent } from '../../../../components/data-input/form-inputs/translation-text-editor/translation-text-editor.component';
import { IconPickerComponent } from '../../../../components/data-input/form-inputs/icon-picker/icon-picker.component';
import { TicketTileFactoryService } from '../../../../factory/ticketTile/ticket-tile-factory.service';
import { DialogService } from '../../../../services/support/dialog/dialog.service';
import {
  ISortimentLayoutAssignmentDialogData,
  SortimentLayoutAssignmentComponent,
} from '../sortiment-layout-assignment/sortiment-layout-assignment.component';

@Component({
  selector: 'aw-sortiment-edit',
  imports: [
    SectionHeadlineComponent,
    ChipComponent,
    ButtonComponent,
    SectionComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    ImageSelectComponent,
    TranslationAccessibilityTextInputComponent,
    TranslationTextInputComponent,
    TranslationTextEditorComponent,
    IconPickerComponent,
  ],
  templateUrl: './sortiment-edit.component.html',
  styleUrl: './sortiment-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortimentEditComponent {
  public readonly ticketForm: InputSignal<FormGroup<ITicketTileForm>> =
    input.required<FormGroup<ITicketTileForm>>();

  public readonly sortimentForm: InputSignal<FormGroup<ISortimentForm>> =
    input.required<FormGroup<ISortimentForm>>();

  public readonly resetTicket: OutputEmitterRef<void> = output<void>();

  public readonly selectedTicket: InputSignal<
    ICmsTicketOverviewItem | undefined
  > = input.required<ICmsTicketOverviewItem | undefined>();
  public readonly isTicketLayoutCompleted: InputSignal<boolean> =
    input.required<boolean>();
  public readonly unassignedLayoutTicketCount: InputSignal<number> =
    input.required<number>();
  protected readonly getTicketSortIcon: typeof getTicketSortIcon =
    getTicketSortIcon;
  protected readonly EntityType: typeof EEntityType = EEntityType;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly ticketTileFactory: TicketTileFactoryService = inject(
    TicketTileFactoryService
  );
  private readonly dialogService: DialogService = inject(DialogService);

  protected openLayoutAssignmentDialog(): void {
    const selectedTicket = this.selectedTicket();

    if (!selectedTicket) {
      return;
    }

    const dialogRef =
      this.dialogService.openDialog<ISortimentLayoutAssignmentDialogData>(
        SortimentLayoutAssignmentComponent,
        {
          headline: this.cmsTextProvider.sortiment.layoutAssignmentTitle,
          selectedTicket,
          layoutAssignments: this.getLayoutAssignments(selectedTicket),
          width: '600px',
        }
      );

    dialogRef.closed.subscribe(result => {
      if (!result?.submit) {
        return;
      }

      this.updateLayoutAssignments(
        selectedTicket,
        result.data.layoutAssignments
      );
    });
  }

  private getLayoutAssignments(
    ticket: ICmsTicketOverviewItem
  ): ILayoutAssignment[] {
    const sortIds = new Set(ticket.sortenExterneReferenzen);

    return this.sortimentForm()
      .controls.layoutAssignments.controls.filter(form =>
        sortIds.has(form.controls.sortId.value)
      )
      .flatMap((form): ILayoutAssignment[] => {
        const layoutId = form.controls.layoutId.value;
        return layoutId === null
          ? []
          : [
              {
                sortId: form.controls.sortId.value,
                layoutId,
                lastEditedAt: form.controls.lastEditedAt.value,
              },
            ];
      });
  }

  private updateLayoutAssignments(
    ticket: ICmsTicketOverviewItem,
    layoutAssignments: ILayoutAssignment[]
  ): void {
    const assignmentsBySortId = new Map(
      layoutAssignments.map(assignment => [assignment.sortId, assignment])
    );
    let hasChanges = false;

    ticket.sortenExterneReferenzen.forEach((sortId: string): void => {
      const assignmentForm =
        this.sortimentForm().controls.layoutAssignments.controls.find(
          (form: FormGroup<ILayoutAssignmentForm>): boolean =>
            form.controls.sortId.value === sortId
        );
      const layoutId = assignmentsBySortId.get(sortId)?.layoutId ?? null;

      if (
        !assignmentForm ||
        assignmentForm.controls.layoutId.value === layoutId
      ) {
        return;
      }

      assignmentForm.controls.layoutId.setValue(layoutId);
      assignmentForm.markAsDirty();
      hasChanges = true;
    });

    if (hasChanges) {
      this.sortimentForm().controls.layoutAssignments.markAsDirty();
      this.sortimentForm().markAsDirty();
    }
  }

  protected addDetail(): void {
    this.ticketForm().controls.details.push(
      this.ticketTileFactory.createTicketTileDetailForm()
    );
    this.updateDetailPositions();
    this.ticketForm().markAsDirty();
  }

  protected removeDetail(index: number): void {
    this.ticketForm().controls.details.removeAt(index);
    this.updateDetailPositions();
    this.ticketForm().markAsDirty();
  }

  protected moveDetail(index: number, newIndex: number): void {
    const details = this.ticketForm().controls.details;
    const detail = details.at(index);

    if (newIndex < 0 || newIndex >= details.length) {
      return;
    }

    details.removeAt(index);
    details.insert(newIndex, detail);
    this.updateDetailPositions();
    this.ticketForm().markAsDirty();
  }

  private updateDetailPositions(): void {
    this.ticketForm().controls.details.controls.forEach((detail, index) => {
      detail.controls.sortPosition.setValue(index, { emitEvent: false });
    });
  }
}
